import { Option } from '@sapphire/result';
import { Nullable } from '@ps/utils';
import {
  mapSurveyLocalization,
  SurveyClassification,
  SurveyLocaleData,
} from '@ps/survey-utils';
import { SurveyChildKey } from './survey-creation-shared-state.model';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from './survey-creation-state.model';
import { DEFAULT_SURVEY_CHILD_KEY } from './survey-creation-state.constants';

/**
 * Shared per-child map mechanic, used identically by every sub-slice keyed
 * by `SurveyChildKey` (`audience`, `core`, and the future `launch` slice) —
 * none of them know about each other's state shape, only their own
 * `Record<SurveyChildKey, T>`.
 */

export type SurveyChildEntry = readonly [
  SurveyChildKey,
  SurveyCreationChildAudienceState,
  SurveyChildRegistryState,
];

/** A child's key: the survey id once created, else a locale key, else empty. */
export function deriveChildKey(
  surveyId: Nullable<number>,
  locale?: SurveyLocaleData,
): SurveyChildKey {
  return (
    surveyId ??
    (locale
      ? mapSurveyLocalization(locale.countryCode, locale.languageShortCode)
      : '')
  );
}

/**
 * Pre-draft only: one entry per locale, keyed via `deriveChildKey` and
 * keeping whatever already exists at that key — or, with no locale chosen
 * yet, a single entry at the default key. `makeDefaultChild` supplies the
 * domain-specific default for a key that doesn't exist yet; it receives the
 * locale so per-locale content (e.g. filtered metadata) can be seeded, and
 * is called with no argument for the no-locale-yet case.
 */
export function buildLocaleKeyedChildren<T>(
  children: Record<SurveyChildKey, T>,
  localeData: SurveyLocaleData[],
  defaultKey: SurveyChildKey,
  makeDefaultChild: (locale?: SurveyLocaleData) => T,
): Record<SurveyChildKey, T> {
  if (!localeData.length) {
    return { [defaultKey]: children[defaultKey] ?? makeDefaultChild() };
  }

  return Object.fromEntries(
    localeData.map((locale) => {
      const key = deriveChildKey(null, locale);
      return [key, children[key] ?? makeDefaultChild(locale)];
    }),
  );
}

/**
 * Re-keys one child's record from `fromKey` to the default (pre-draft)
 * key, keeping its content — a no-op if `fromKey` is already the default
 * key or nothing exists at `fromKey` yet.
 */
export function delegateRecordToDefaultChild<T>(
  children: Record<SurveyChildKey, T>,
  fromKey: SurveyChildKey,
): Record<SurveyChildKey, T> {
  if (fromKey === DEFAULT_SURVEY_CHILD_KEY || !(fromKey in children)) {
    return children;
  }

  const { [fromKey]: delegatedChild, ...rest } = children;
  return { ...rest, [DEFAULT_SURVEY_CHILD_KEY]: delegatedChild };
}

/** The single child a per-child *read* should resolve to. */
export function resolveActiveChildKey<T>(
  children: Record<SurveyChildKey, T>,
  activeChildKey: Nullable<SurveyChildKey>,
  defaultKey: SurveyChildKey,
): SurveyChildKey {
  return activeChildKey ?? Object.keys(children)[0] ?? defaultKey;
}

/**
 * Which children a per-child *write* targets — a decision each action makes
 * explicitly, not an accident of whether `activeChildKey` happens to be
 * `null`. `ACTIVE` still falls back to every child when there is no active
 * one yet (pre-draft, before a child is singled out) — that fallback is now
 * a documented policy of `ACTIVE` itself, not a side effect callers have to
 * know about.
 */
export type SurveyChildScope =
  | { kind: 'active' }
  | { kind: 'all' }
  | { kind: 'keys'; keys: readonly SurveyChildKey[] };

export const CHILD_SCOPE = {
  ACTIVE: { kind: 'active' } as const,
  ALL: { kind: 'all' } as const,
  keys: (keys: readonly SurveyChildKey[]): SurveyChildScope => ({
    kind: 'keys',
    keys,
  }),
};

/** An action payload that targets a `SurveyChildScope` — the dispatching facade method defaults it to `CHILD_SCOPE.ACTIVE` when the caller doesn't pass one, so the reducer always gets an explicit scope. */
export type WithScope<T> = T & { scope: SurveyChildScope };

/** Decides a `SurveyChildScope` from the workflow's `kind` — e.g. metadata fans out to every child for grouped MC, but stays active-only otherwise. */
export type ChildScopeRule = (
  kind: Nullable<SurveyClassification>,
) => SurveyChildScope;

export function resolveScopedChildKeys<T>(
  children: Record<SurveyChildKey, T>,
  activeChildKey: Nullable<SurveyChildKey>,
  scope: SurveyChildScope,
): SurveyChildKey[] {
  switch (scope.kind) {
    case 'active':
      return Option.from(activeChildKey)
        .map((key) => [key])
        .unwrapOr(Object.keys(children));
    case 'all':
      return Object.keys(children);
    case 'keys':
      return [...scope.keys];
  }
}

export type ChildMapPatch<T> =
  | Partial<T>
  | ((child: T, key: SurveyChildKey) => Partial<T>);

/** Patches one child's fields, applying a static or per-child function patch. */
function patchChild<T>(
  child: T,
  key: SurveyChildKey,
  patch: ChildMapPatch<T>,
): T {
  const patchValues = typeof patch === 'function' ? patch(child, key) : patch;
  return { ...child, ...patchValues };
}

export function patchChildrenMap<T>(
  children: Record<SurveyChildKey, T>,
  activeChildKey: Nullable<SurveyChildKey>,
  scope: SurveyChildScope,
  patch: ChildMapPatch<T>,
  defaultChild: T,
): Record<SurveyChildKey, T> {
  const patchedChildren = { ...children };

  for (const key of resolveScopedChildKeys(children, activeChildKey, scope)) {
    const child = children[key] ?? defaultChild;
    patchedChildren[key] = patchChild(child, key, patch);
  }

  return patchedChildren;
}
